import React, { useState, useEffect, useRef } from 'react';
import '../css/layout.css';
import '../css/utilities.css';
import '../css/citySearch.css';
import hotelsData from '../data/hotels.json';
import { getActiveServices } from '../api/services';

function CitySearch({ onSearch, activeTab, onTabChange, allServices }) {
  const [selectedRegion, setSelectedRegion] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [services, setServices] = useState(allServices || []);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');

  // Helper functions to convert between formats
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  };

  const formatDateForURL = (dateString) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${day}-${month}-${year}`;
  };
  const [guests, setGuests] = useState(2);
  const [promoCode, setPromoCode] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Update services when allServices prop changes
  useEffect(() => {
    if (allServices && allServices.length > 0) {
      // Filter to only include experience and event services for the dropdown
      const dropdownServices = allServices.filter(service => 
        service.category === 'experience' || service.category === 'event'
      );
      setServices(dropdownServices);
    }
  }, [allServices]);

  const handleSearch = () => {
    if (onSearch) {
      onSearch({ region: selectedRegion, query: searchTerm });
    }
  };

  // Filter hotels and services based on search term and active tab
  useEffect(() => {
    if (activeTab === 'experience' || activeTab === 'event') {
      if (searchTerm.trim()) {
        const filtered = services.filter(service =>
          (service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            service.description.toLowerCase().includes(searchTerm.toLowerCase())) &&
          service.category === activeTab
        );
        setFilteredServices(filtered);
      } else {
        // Show all services for both experience and event tabs in the dropdown
        const categoryFiltered = services.filter(service => service.category === activeTab);
        setFilteredServices(categoryFiltered);
      }
    } else {
      if (searchTerm.trim()) {
        const filtered = hotelsData.filter(hotel =>
          hotel.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          hotel.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
          hotel.location.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredHotels(filtered);
      } else {
        setFilteredHotels(hotelsData);
      }
    }
  }, [searchTerm, activeTab, services]);

  // Clear selections and search term when switching tabs
  useEffect(() => {
    setSearchTerm('');
    setSelectedHotel(null);
    setSelectedService(null);
    // Don't automatically show dropdown - let user click to show it
    setShowDropdown(false);
  }, [activeTab]);

  // Handle input focus - show dropdown
  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  // Handle input change
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    // Clear selected item if user manually edits the search term
    if (activeTab === 'experience' || activeTab === 'event') {
      if (selectedService && e.target.value !== selectedService.title) {
        setSelectedService(null);
      }
    } else {
      if (selectedHotel && e.target.value !== selectedHotel.title) {
        setSelectedHotel(null);
      }
    }
  };

  // Handle hotel selection - just fill the input, don't redirect
  const handleHotelSelect = (hotel) => {
    setSearchTerm(hotel.title);
    setSelectedHotel(hotel);
    setSelectedService(null);
    setShowDropdown(false);
  };

  // Handle service selection - for events trigger search, for experiences redirect to booking
  const handleServiceSelect = (service) => {
    setSearchTerm(service.title);
    setSelectedService(service);
    setSelectedHotel(null);
    setShowDropdown(false);

    if (activeTab === 'experience') {
      // Debug: Log the service object to see its structure
      console.log('Service object:', service);

      // Use both _id and id as fallback to handle any inconsistency
      const serviceId = service.id || service._id;
      console.log('Using serviceId:', serviceId);

      if (!serviceId) {
        console.error('No valid service ID found:', service);
        alert('Error: Service ID not found. Please try again.');
        return;
      }

      // Use exact same logic as ServiceCard's handleBookNow
      const bookingUrl = `/booking?serviceId=${serviceId}&title=${encodeURIComponent(service.title)}`;
      window.open(bookingUrl, '_blank');
    } else if (activeTab === 'event') {
      // For events, redirect to booking like experiences
      const serviceId = service.id || service._id;
      console.log('Using serviceId:', serviceId);

      if (!serviceId) {
        console.error('No valid service ID found:', service);
        alert('Error: Service ID not found. Please try again.');
        return;
      }

      // Use exact same logic as ServiceCard's handleBookNow
      const bookingUrl = `/booking?serviceId=${serviceId}&title=${encodeURIComponent(service.title)}`;
      window.open(bookingUrl, '_blank');
    }
  };

  // Handle check availability with validation
  const handleCheckAvailability = () => {
    // Only validate for stay tab since experience/event services redirect immediately when clicked
    if (activeTab === 'experience' || activeTab === 'event') {
      alert(`Please select ${activeTab === 'event' ? 'an event' : 'an experience'} from the dropdown above`);
      return;
    }

    if (!selectedHotel) {
      alert('Please select a hotel from the dropdown');
      return;
    }

    if (!checkIn) {
      alert('Please select a check-in date');
      return;
    }

    if (!checkOut) {
      alert('Please select a check-out date');
      return;
    }

    if (!guests || guests < 1) {
      alert('Please specify the number of guests (minimum 1)');
      return;
    }

    // Validate check-in is before check-out
    if (new Date(formatDateForInput(checkIn)) >= new Date(formatDateForInput(checkOut))) {
      alert('Check-out date must be after check-in date');
      return;
    }

    // Build URL with parameters
    let bookingUrl = selectedHotel.bookingUrl;

    // Add parameters if the booking URL supports them
    const urlParams = new URLSearchParams();
    urlParams.set('checkin', formatDateForURL(checkIn));
    urlParams.set('checkout', formatDateForURL(checkOut));
    urlParams.set('guests', guests);
    urlParams.set('city', selectedHotel.city);
    urlParams.set('location', selectedHotel.location);

    // Add category based on active tab
    if (activeTab === 'stay') {
      urlParams.set('category', 'accommodation');
      urlParams.set('type', 'hotel,resort,vacation_house');
    } else if (activeTab === 'experience') {
      urlParams.set('category', 'experience');
      urlParams.set('type', 'tours,team_building,activities');
    } else if (activeTab === 'event') {
      urlParams.set('category', 'event');
      urlParams.set('type', 'tours,team_building,activities');
    }

    if (promoCode.trim()) {
      urlParams.set('promo', promoCode.trim());
    }

    // Append parameters to URL
    const separator = bookingUrl.includes('?') ? '&' : '?';
    const finalUrl = `${bookingUrl}${separator}${urlParams.toString()}`;

    // Open booking URL in new tab
    window.open(finalUrl, '_blank');
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <section className="city-search">
      <div className='city-search__title'>
        <div className="city-tab-container">
          <button
            className={`city-tab-button ${activeTab === 'stay' ? 'active' : ''}`}
            onClick={() => onTabChange && onTabChange('stay')}
          >
            Stay
          </button>
          <div className="city-tab-separator">|</div>

          <button
            className={`city-tab-button ${activeTab === 'experience' ? 'active' : ''}`}
            onClick={() => onTabChange && onTabChange('experience')}
          >
            Experience
          </button>
          <div className="city-tab-separator">|</div>

          <button
            className={`city-tab-button ${activeTab === 'event' ? 'active' : ''}`}
            onClick={() => onTabChange && onTabChange('event')}
          >
            Event
          </button>
        </div>

      </div>
      {/* Transportation | Shop */}
      {activeTab === 'stay' && (
        <div className="city-search__form">
        <div className="city-search__group" ref={dropdownRef}>
          <label>
            {activeTab === 'experience' ? 'Select Activities:' :
              activeTab === 'event' ? 'Select Event:' :
                'Select hotel/City:'}
          </label>
          <div className="hotel-search-container">
            <input
              ref={inputRef}
              type="text"
              placeholder={
                activeTab === 'experience' ? 'Find Activities...' :
                  activeTab === 'event' ? 'Find events...' :
                    'Find hotel...'
              }
              className={`input-field ${(selectedHotel || selectedService) ? 'hotel-selected' : ''}`}
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
            />
            {showDropdown && (activeTab === 'experience' || activeTab === 'event') && filteredServices.length > 0 && (
              <div className="hotel-dropdown">
                {filteredServices.map((service) => (
                  <div
                    key={service.id}
                    className="hotel-dropdown-item"
                    onClick={() => handleServiceSelect(service)}
                  >
                    <div className="hotel-title">{service.title}</div>
                    <div className="hotel-location">{service.description || service.category}</div>
                  </div>
                ))}
              </div>
            )}
            {showDropdown && activeTab !== 'experience' && activeTab !== 'event' && filteredHotels.length > 0 && (
              <div className="hotel-dropdown">
                {filteredHotels.map((hotel) => (
                  <div
                    key={hotel.id}
                    className="hotel-dropdown-item"
                    onClick={() => handleHotelSelect(hotel)}
                  >
                    <div className="hotel-title">{hotel.title}</div>
                    <div className="hotel-location">{hotel.location}, {hotel.city}</div>
                  </div>
                ))}
              </div>
            )}
            {showDropdown && searchTerm && (
              ((activeTab === 'experience' || activeTab === 'event') && filteredServices.length === 0) ||
              (activeTab !== 'experience' && activeTab !== 'event' && filteredHotels.length === 0)
            ) && (
                <div className="hotel-dropdown">
                  <div className="hotel-dropdown-item no-results">
                    No {(activeTab === 'experience' || activeTab === 'event') ? (activeTab + 's') : 'hotels'} found matching "{searchTerm}"
                  </div>
                </div>
              )}
          </div>
        </div>

        {activeTab !== 'event' && (
          <div className="city-search__group">
            <label><i className="fas fa-calendar-alt"></i> Check-in:</label>
          <div className="custom-date-wrapper" style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              className="input-field"
              value={checkIn}
              readOnly
              onClick={() => document.getElementById('hidden-checkin').click()}
              style={{ cursor: 'pointer', paddingRight: '35px' }}
              required
            />
            <input
              id="hidden-checkin"
              type="date"
              style={{
                position: 'absolute',
                right: '0px',
                top: '0px',
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
                zIndex: 2
              }}
              value={formatDateForInput(checkIn)}
              onChange={(e) => setCheckIn(formatDateForDisplay(e.target.value))}
              min={new Date().toISOString().split('T')[0]}
              onClick={(e) => {
                if ('showPicker' in HTMLInputElement.prototype) {
                  try {
                    e.target.showPicker();
                  } catch (error) {
                    console.error('Error opening date picker:', error);
                  }
                }
              }}
            />
            <i
              className="fas fa-calendar-alt"
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#666',
                fontSize: '16px',
                cursor: 'pointer',
                pointerEvents: 'auto', // Ensure the icon is clickable
                zIndex: 1,
              }}
              onClick={() => document.getElementById('hidden-checkin').click()} // Open date picker on icon click
            ></i>
            </div>
          </div>
        )}

        {activeTab !== 'event' && (
          <div className="city-search__group">
            <label><i className="fas fa-calendar-alt"></i> Check-out:</label>
          <div className="custom-date-wrapper" style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              className="input-field"
              value={checkOut}
              readOnly
              onClick={() => document.getElementById('hidden-checkout').click()}
              style={{ cursor: 'pointer', paddingRight: '35px' }}
              required
            />

            <input
              id="hidden-checkout"
              type="date"
              style={{
                position: 'absolute',
                right: '0px',
                top: '0px',
                width: '100%',  // ✅ Make entire field clickable
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
                zIndex: 2
              }}
              value={formatDateForInput(checkOut)}
              onChange={(e) => setCheckOut(formatDateForDisplay(e.target.value))}
              min={formatDateForInput(checkIn) || new Date().toISOString().split('T')[0]}
              onClick={(e) => {
                if ('showPicker' in HTMLInputElement.prototype) {
                  try {
                    e.target.showPicker();
                  } catch (error) {
                    console.error('Error opening date picker:', error);
                  }
                }
              }}
            />

            <i
              className="fas fa-calendar-alt"
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#666',
                fontSize: '16px',
                cursor: 'pointer',
                pointerEvents: 'auto',
                zIndex: 3
              }}
              onClick={() => document.getElementById('hidden-checkout').click()}
            ></i>
          </div>
          </div>
        )}

        <div className="city-search__group">
          <label><i className="fas fa-user"></i> Guest:</label>
          <input
            type="number"
            min="1"
            className="input-field"
            value={guests}
            onChange={(e) => setGuests(parseInt(e.target.value) || 0)}
            required
          />
        </div>

        <div className="city-search__group">
          <label><i className="fas fa-ticket-alt"></i> Promo code:</label>
          <input
            type="text"
            placeholder="Promo code"
            className="input-field"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
          />
        </div>

        <button
          className="check-btn"
          onClick={handleCheckAvailability}
          type="button"
        >
          Check Availability
        </button>
        </div>
      )}
    </section>
  );
}

export default CitySearch;